// =============================================================================
// sales-preferences.ts — Contrato Zod compartilhado das preferências de
// contato (`contact_preferences`, F34-S04, doc 26 §8/§22).
//
// Fonte única consumida pela API (validação de request/response,
// modules/sales/preferences) e pelo frontend (F34-S05) — nenhum dos dois
// lados redeclara enums ou o shape de resposta.
//
// "Efetivo" vs "bruto": `contact_preferences` é um LOG append-only e
// `expires_at` NÃO entra no predicado do índice único (Postgres proíbe
// função volátil `now()` em predicado de índice, ver
// apps/api/src/db/schema/contactPreferences.ts) — um bloqueio expirado
// continua sendo a linha ATIVA (deleted_at IS NULL) até que uma nova
// mudança de estado a substitua. Por isso o campo `state` desta resposta é
// SEMPRE o estado EFETIVO (já considerando expiração): um bloqueio com
// `expiresAt` no passado aparece como `state: 'allowed'` aqui, mesmo que a
// linha ativa no banco ainda tenha `state='blocked'`. O consumidor nunca
// precisa reimplementar essa conta.
// =============================================================================
import { z } from 'zod';
import { CONTACT_CHANNELS, ContactChannelSchema, ContactPreferenceStateSchema } from './sales.js';
// `CONTACT_PREFERENCE_STATES`/`ContactPreferenceStateSchema`/`ContactPreferenceState`
// já existem em sales.ts (F34-S01) — usados aqui via import direto, NUNCA
// redeclarados nem re-exportados: `export *` em index.ts (que já reexporta
// './sales.js') colidiria com a fonte original se este arquivo também os
// exportasse.
// ---------------------------------------------------------------------------
// Canais de voz — derivado de CONTACT_CHANNELS, nunca reescrito à mão.
//
// `invalid_number` (SALES_OUTCOME_META, sales.ts) bloqueia apenas os canais
// de telefonia — `phone_manual` + `phone_integrated`. Esta constante é a
// ÚNICA fonte do que "phone_only" significa; nenhum consumidor deve
// hardcodar `['phone_manual', 'phone_integrated']` (achado da revisão de
// segurança de F32-S01 — a lista só existia em prosa/JSDoc).
// ---------------------------------------------------------------------------
export const PHONE_ONLY_CONTACT_CHANNELS = CONTACT_CHANNELS.filter((channel) => channel === 'phone_manual' || channel === 'phone_integrated');
// ---------------------------------------------------------------------------
// Params — :leadId / :channel
// ---------------------------------------------------------------------------
export const ContactPreferenceLeadParamSchema = z.object({
    leadId: z.string().uuid().describe('UUID do lead titular das preferências de contato'),
});
export const ContactPreferenceChannelParamSchema = ContactPreferenceLeadParamSchema.extend({
    channel: ContactChannelSchema,
});
// ---------------------------------------------------------------------------
// Response — estado (efetivo) de um canal
// ---------------------------------------------------------------------------
export const ContactPreferenceChannelStateSchema = z.object({
    channel: ContactChannelSchema,
    /**
     * Estado EFETIVO — já considera expiração automática (ver header do
     * arquivo). `'allowed'` quando não há registro algum (default do sistema
     * — nenhum titular optou por bloquear este canal ainda).
     */
    state: ContactPreferenceStateSchema,
    /** `contact_preferences.id` do registro ativo. null = sem registro (equivale a 'allowed' por omissão). */
    id: z.string().uuid().nullable(),
    /** Motivo da preferência. Texto livre — PII potencial (doc 17 §8.3). */
    reason: z.string().nullable(),
    /** Origem (ex: 'manual', 'outcome:do_not_contact'). Categórico/curto. */
    source: z.string().nullable(),
    /** `users.id` de quem criou o registro. null = origem automática (outcome). */
    createdByUserId: z.string().uuid().nullable(),
    /** Validade opcional do bloqueio/permissão. null = sem prazo definido. */
    expiresAt: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Response — GET /api/sales/leads/:leadId/preferences
//
// Sempre retorna UMA entrada por canal de CONTACT_CHANNELS (mesmo sem
// registro no banco) — o consumidor nunca precisa saber que "ausência de
// linha" equivale a "allowed".
// ---------------------------------------------------------------------------
export const ContactPreferencesListResponseSchema = z.object({
    leadId: z.string().uuid(),
    preferences: z.array(ContactPreferenceChannelStateSchema),
});
// ---------------------------------------------------------------------------
// Body — PUT /api/sales/leads/:leadId/preferences/:channel
// ---------------------------------------------------------------------------
export const ContactPreferenceSetBodySchema = z.object({
    state: ContactPreferenceStateSchema,
    /** Motivo da mudança. Texto livre — PII potencial (doc 17 §8.3). */
    reason: z.string().min(1).max(500).optional(),
    /** Validade opcional (bloqueio/permissão temporária). Omitir = sem prazo definido. */
    expiresAt: z.string().datetime({ offset: true }).optional(),
});
//# sourceMappingURL=sales-preferences.js.map